<template>
	<section class="journey-list-travel">
		<div class="journey-list-travel__main">
			<div class="journey-list-travel__tags" :key="routeCodes.join('-')">
				<span v-for="(routeCode, index) in displayedRouteCodes" :key="index" class="journey-list-travel__service-tag">
					<svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
						<rect width="23" height="23" rx="11.5" fill="#2a347a" />
						<g clip-path="url(#clip0_937_3883)">
							<path
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M4.93457 15.0883C4.93457 15.79 5.24557 16.42 5.732 16.8586V18.278C5.732 18.7166 6.09084 19.0754 6.52942 19.0754H7.32685C7.76543 19.0754 8.12427 18.7166 8.12427 18.278V17.4806H14.5037V18.278C14.5037 18.7166 14.8625 19.0754 15.3011 19.0754H16.0985C16.5371 19.0754 16.896 18.7166 16.896 18.278V16.8586C17.3824 16.42 17.6934 15.79 17.6934 15.0883V7.11402C17.6934 4.32303 14.8386 3.92432 11.314 3.92432C7.78936 3.92432 4.93457 4.32303 4.93457 7.11402V15.0883ZM7.72556 15.8857C7.0637 15.8857 6.52942 15.3514 6.52942 14.6896C6.52942 14.0277 7.0637 13.4934 7.72556 13.4934C8.38743 13.4934 8.9217 14.0277 8.9217 14.6896C8.9217 15.3514 8.38743 15.8857 7.72556 15.8857ZM14.9024 15.8857C14.2405 15.8857 13.7063 15.3514 13.7063 14.6896C13.7063 14.0277 14.2405 13.4934 14.9024 13.4934C15.5643 13.4934 16.0985 14.0277 16.0985 14.6896C16.0985 15.3514 15.5643 15.8857 14.9024 15.8857ZM16.0985 11.1012H6.52942V7.11402H16.0985V11.1012Z"
								fill="white"
							/>
						</g>
						<defs>
							<clipPath id="clip0_937_3883">
								<rect width="15.1511" height="15.1511" fill="white" transform="translate(3.92432 3.92432)" />
							</clipPath>
						</defs>
					</svg>
					{{ routeCode }}
				</span>
			</div>
			<div class="journey-list-travel__time">
				<div class="journey-list-travel__time-item">
					{{ formatTime(departureTimestamp) }}
				</div>
				<div class="journey-list-travel__line-container">
					<div class="journey-list-travel__line"></div>
					<div class="journey-list-travel__duration">
						{{ formatDuration(travelDuration) }}
					</div>
					<div class="journey-list-travel__line"></div>
				</div>
				<div class="journey-list-travel__time-item">
					{{ formatTime(arrivalTimestamp) }}
				</div>
			</div>
		</div>
		<div class="journey-list-travel__price">
			<span v-if="hasTravelInfo" class="journey-list-travel__circle">
				<svg class="journey-list-travel__icon">
					<use :xlink:href="`#${info}`" />
				</svg>
			</span>
			<div v-if="bundles.length" class="journey-list-travel__price-text">
				<span v-if="showPriceLabel" class="journey-list-travel__price-label">
					{{ $t("travelPlanner.journeyList.travel.price") }}
				</span>
				{{ getCheapestPrice(bundles) }}
			</div>
			<div class="journey-list-travel__exception" v-else>
				{{ getCheapestPrice(bundles) }}
			</div>
			<svg class="journey-list-travel__price-icon">
				<use :xlink:href="`#${chevron}`" />
			</svg>
		</div>
	</section>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { JourneyTicketBundle, ExtendedLeg, JourneyDetailsLeg, JourneyStation } from "@/types/searchTypes";
import { formatTime } from "@/utils/dateUtils";
import { searchServices } from "@/services/journeyService";
import buss from "@/assets/icons/buss.svg";
import chevron from "@/assets/icons/chevron.svg";
import info from "@/assets/icons/info.svg";
import { useMyTravelStore } from "@/stores/myTravelStore";

export default defineComponent({
	name: "JourneyListTravel",
	props: {
		routeCodes: {
			type: Array as PropType<string[]>,
			required: true,
		},
		departureTimestamp: {
			type: String,
			required: true,
		},
		arrivalTimestamp: {
			type: String,
			required: true,
		},
		travelDuration: {
			type: String,
			required: true,
		},
		bundles: {
			type: Array as PropType<JourneyTicketBundle[]>,
			required: true,
		},
		legs: {
			type: Array as PropType<ExtendedLeg[]>,
			required: true,
			default: () => [],
		},
		formattedResponse: {
			type: Array as PropType<JourneyDetailsLeg[]>,
			required: false,
			default: () => [],
		},
	},
	setup(props) {
		const { t, locale } = useI18n();
		const myTravelStore = useMyTravelStore();
		const isLoading = ref(false);
		const formattedResponse = ref<JourneyDetailsLeg[]>([]);

		const fetchData = async () => {
			if (isLoading.value || !props.legs?.length) {
				return;
			}

			try {
				isLoading.value = true;
				const allLegs: JourneyDetailsLeg[] = [];
				for (const leg of props.legs) {
					if (!leg.service_name || !leg.service_schedule_date) {
						continue;
					}

					const data = await searchServices(leg.service_name, leg.service_schedule_date.split("T")[0]);

					if (!data || !data.data || !data.data.services) {
						console.error("[JourneyListTravel] No services found for leg:", leg.service_name);
						throw new Error("No services found in the response");
					}

					const departureStation: JourneyStation = {
						name: leg.departure_station.name,
						timestamp: "",
					};

					const arrivalStation: JourneyStation = {
						name: leg.arrival_station.name,
						timestamp: "",
					};

					allLegs.push({
						departureStation,
						arrivalStation,
						travelInfo: leg.travel_info,
					});
				}

				formattedResponse.value = allLegs;
			} catch (error) {
				console.error("[JourneyListTravel] fetchData error:", error);
			} finally {
				isLoading.value = false;
			}
		};

		const displayedRouteCodes = computed(() => {
			const codes = Array.isArray(props.routeCodes) ? props.routeCodes : [];
			return codes.slice(0, 2);
		});

		const showPriceLabel = computed(() => {
			return props.bundles.length > 0 && !props.legs?.some((leg) => leg.sales_restrictions?.length);
		});

		const hasTravelInfo = computed(() => {
			const hasInfo = formattedResponse.value?.some((leg) => (leg.travelInfo?.length ?? 0) > 0);
			return hasInfo;
		});

		function formatDuration(duration: string): string {
			const match = duration.match(/PT(\d+H)?(\d+M)?/);
			if (!match) return duration;

			const hours = match[1] ? parseInt(match[1].replace("H", "")) : 0;
			const minutes = match[2] ? parseInt(match[2].replace("M", "")) : 0;

			const hourUnit = locale.value === "no" ? "t" : "h";
			return `${hours}${hourUnit} ${minutes}m`;
		}

		function getCheapestPrice(bundles: JourneyTicketBundle[]): string {
			if (!bundles.length) {
				if (props.legs?.length) {
					for (const leg of props.legs) {
						if (leg.sales_restrictions?.length) {
							return t(`travelPlanner.journeyList.travel.${leg.sales_restrictions[0].reason_code}`);
						}
					}
				}
				return t("travelPlanner.journeyList.travel.full");
			}

			const isRebooking = myTravelStore.isRebooking;

			if (isRebooking) {
				const pricesToBePaid = bundles.map((bundle) => bundle.total_price_to_be_paid || 0);
				const cheapestPriceToBePaid = Math.min(...pricesToBePaid);
				return `${cheapestPriceToBePaid}${t("common.currency")}`;
			} else {
				const prices = bundles.map((bundle) => bundle.price);
				const cheapestPrice = Math.min(...prices);
				return `${cheapestPrice}${t("common.currency")}`;
			}
		}

		onMounted(() => {
			if (props.legs?.length) {
				fetchData();
			}
		});

		watch(
			() => props.legs,
			(newLegs, oldLegs) => {
				if (newLegs?.length && JSON.stringify(newLegs) !== JSON.stringify(oldLegs)) {
					fetchData();
				}
			},
			{ deep: true }
		);

		return {
			buss,
			chevron,
			info,
			displayedRouteCodes,
			showPriceLabel,
			formatTime,
			formatDuration,
			getCheapestPrice,
			hasTravelInfo,
			t,
		};
	},
});
</script>

<style lang="scss" scoped>
.journey-list-travel {
	display: flex;
	flex-direction: row;
	align-items: stretch;
	flex-grow: 1;

	&__main {
		padding: rem(16) 16px;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: center;
		flex-grow: 1;

		@media screen and (min-width: $breakpoint-xs) {
			padding: rem(32) 16px;
		}

		@media screen and (min-width: $breakpoint-md) {
			padding-right: 32px;
			padding-left: 32px;
		}
	}

	&__circle {
		width: 22px;
		height: 22px;
		background-color: $clr-tertiary;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 8px;

		svg {
			width: 100%;
			height: 100%;
			color: $clr-text;
		}
	}

	&__tags {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	&__service-tag {
		@include title-medium;
		border-radius: 19px;
		border: 1px solid $clr-primary-50;
		padding: rem(6) 8px;
		color: $clr-primary-500;
		display: inline-flex;
		align-items: center;
		align-self: start;

		svg {
			width: 23px;
			height: 23px;
			margin-right: 8px;
			color: $clr-primary-500;
			display: inline-block;
		}
	}

	&__time {
		margin-top: rem(24);
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;

		&-item {
			@include title-medium;

			@media screen and (min-width: $breakpoint-xs) {
				@include headline-medium;
			}
		}
	}

	&__line-container {
		display: flex;
		align-items: center;
		flex-grow: 1;
		margin: 4px 0 0;
	}

	&__line {
		border-bottom: 1px solid $clr-primary-100;
		flex-grow: 1;
		margin: 4px 8px 0;
	}

	&__duration {
		@include body-large;
		color: $clr-text;
		white-space: nowrap;
	}

	&__price {
		color: $clr-primary;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: space-between;
		border-left: dashed 1px $clr-primary-100;
		padding: rem(24) 16px rem(16);
		width: rem(80);
		flex-shrink: 0;

		@media screen and (min-width: $breakpoint-xs) {
			padding: rem(32) 16px;
			width: rem(100);
		}

		@media screen and (min-width: $breakpoint-md) {
			padding-right: 32px;
			padding-left: 32px;
			width: rem(110);
		}

		&-text {
			align-self: center;
			@include title-medium;
			word-break: break-word;
			display: inline-block;
			text-align: center;
			width: 100%;

			@media screen and (min-width: $breakpoint-xs) {
				@include headline-medium;
			}
		}

		span {
			@include body-large;
		}

		&-icon {
			width: 24px;
			height: 24px;
			transform: rotate(180deg);
			transition: transform 0.3s ease;
		}
	}

	&__exception {
		@include title-medium;
		word-break: break-word;
		display: inline-block;
		text-align: center;

		@media screen and (min-width: $breakpoint-md) {
			@include headline-small;
		}
	}
}

details[open] .journey-list-travel__price-icon {
	transform: rotate(0deg);
}
</style>
